// Shared presentational components: sync chip, agenda cards, error boundary.
import { Component, useEffect, useRef, useState } from "react";
import { EXP_TYPES, OT_CODES, SHORT_CODE } from "./constants";
import { calcMins, entryHrs, fmtHrs, fmtTime, isAbs, money, monthLabel } from "./calc";
import { MIcon } from "./icons";

// Glanceable sync state — synced (calm) / syncing / offline / storage warning.
// Rendered as a button: tapping it triggers a manual sync (also the retry
// path when the chip shows Offline — navigator.onLine can be stale).
export function SyncChip({ state, sub, onClick }) {
  const meta = {
    synced:  { cls: "ok",   ico: "✓", txt: "Synced" },
    syncing: { cls: "busy", ico: "⟳", txt: "Syncing…" },
    offline: { cls: "off",  ico: "!", txt: "Offline" },
    storage: { cls: "off",  ico: "!", txt: "Cache full" },
  }[state] || { cls: "ok", ico: "✓", txt: "Synced" };
  return (
    <button type="button" className={"syncchip " + meta.cls} onClick={onClick}
            aria-label={"Sync now — " + meta.txt + " · " + sub} title={"Sync now · " + meta.txt + " · " + sub}>
      <span className="sync-ico" style={{fontSize:9,fontWeight:800,lineHeight:1}}>
        {state === "syncing" ? <span className="sync-spin">⟳</span> : meta.ico}
      </span>
      <span className="sync-txt">{meta.txt}</span>
      <span className="sync-sep">·</span>
      <span className="sync-time">{sub}</span>
    </button>
  );
}

// In-page camera for receipt capture — renders our own <video> preview via
// getUserMedia instead of delegating to the OS's native camera picker (which
// previously showed a black viewfinder on some phones; see TODO item 10).
// phase: "live" (streaming) | "review" (frozen frame, confirm/retake) | "error"
export function CameraCapture({ onCapture, onClose }) {
  const videoRef = useRef(null);
  const canvasRef = useRef(null);
  const streamRef = useRef(null);
  const [phase, setPhase] = useState("live");
  const [frameUrl, setFrameUrl] = useState(null); // data URL of the frozen frame, review phase only
  // "denied": browser has this site set to blocked — no prompt will show again
  // until the user flips it in site settings. "other": no camera, insecure
  // context, or any other getUserMedia failure.
  const [errorKind, setErrorKind] = useState("other");

  useEffect(() => {
    let cancelled = false;
    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      setErrorKind("other");
      setPhase("error");
      return;
    }
    navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } })
      .then(stream => {
        if (cancelled) { stream.getTracks().forEach(t => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      })
      .catch(err => {
        if (cancelled) return;
        setErrorKind(err && err.name === "NotAllowedError" ? "denied" : "other");
        setPhase("error");
      });
    return () => {
      cancelled = true;
      if (streamRef.current) streamRef.current.getTracks().forEach(t => t.stop());
    };
  }, []);

  function takeShot() {
    const video = videoRef.current, canvas = canvasRef.current;
    if (!video || !canvas) return;
    canvas.width = video.videoWidth;
    canvas.height = video.videoHeight;
    canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
    setFrameUrl(canvas.toDataURL("image/jpeg", 0.92));
    setPhase("review");
  }

  function retake() {
    setFrameUrl(null);
    setPhase("live");
  }

  function usePhoto() {
    if (!frameUrl) return;
    fetch(frameUrl).then(r => r.blob()).then(blob => { if (blob) onCapture(blob); });
  }

  return (
    <div className="a-sheet cam-sheet">
      <button type="button" className="cam-close" aria-label="Close camera" onClick={onClose}>
        <MIcon n="x" s={18} />
      </button>

      {phase === "error" ? (
        <div className="cam-error">
          <p>{errorKind === "denied"
            ? "Camera access is blocked for this site. Open your browser's site settings (tap the lock/info icon in the address bar, or your browser's Settings > Site permissions) and allow Camera, then try again."
            : "Couldn't access the camera — check your browser's camera permission for this site."}</p>
          <button type="button" className="btn-ghost" onClick={onClose}>Close</button>
        </div>
      ) : (
        <>
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            className="cam-video"
            style={{ display: phase === "live" ? "block" : "none" }}
          />
          {phase === "review" && (
            <img src={frameUrl} alt="Captured receipt" className="cam-video" />
          )}
          <canvas ref={canvasRef} style={{ display: "none" }} />

          {phase === "live" && (
            <button type="button" className="cam-shutter" aria-label="Take photo" onClick={takeShot} />
          )}
          {phase === "review" && (
            <div className="cam-review-bar">
              <button type="button" className="btn-ghost" onClick={retake}>Retake</button>
              <button type="button" className="btn-primary" onClick={usePhoto}>Use photo</button>
            </div>
          )}
        </>
      )}
    </div>
  );
}

// OTTracker expense card for the agenda feed
export function ExpCard({ x, onEdit, onDelete, onReceipt }) {
  const t = EXP_TYPES[x.type] || EXP_TYPES.other;
  return (
    <div className="c-card x-card" onClick={onEdit} style={{cursor:"pointer"}}>
      <div className="c-card-head">
        <span className="typepill k-exp">
          <span className="exp-ico"><MIcon n={t.icon} s={13} /></span>{t.label}
        </span>
        <span style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="x-amt">{money(x.amount)}</span>
          {onDelete&&<button className="x-card-del" aria-label="Delete" onClick={ev=>{ev.stopPropagation();onDelete();}}>×</button>}
        </span>
      </div>
      {(x.project||x.reason)&&(
        <div className="x-body">
          {x.project&&(
            <div className="x-meta">
              <span className="c-tag">#{x.project}</span>
              <span className="x-month-tag">{monthLabel(x.date)} claim</span>
            </div>
          )}
          {x.costCentre&&(
            <div className="x-meta" style={{marginTop:x.project?0:4}}>
              <span className={"c-tag cc cc-"+x.costCentre}><span className="cc-dot"/>{x.costCentre==="60"?"Fire Control":"Resources"} · {x.costCentre==="60"?"160060":"160062"}</span>
            </div>
          )}
          {x.reason&&<div className="c-reason">“{x.reason}”</div>}
        </div>
      )}
      {x.receipt?(
        <div className="x-receipt" onClick={ev=>{ev.stopPropagation();onReceipt();}} style={{cursor:"zoom-in"}}>
          <div className="receipt-thumb" style={{backgroundImage:`url(${x.receipt})`,backgroundSize:"cover",backgroundPosition:"center"}} />
          <span className="x-receipt-lab">Receipt attached</span>
          <MIcon n="chevR" s={15} />
        </div>
      ):x.receiptOmitted?(
        // Receipt is safe in PocketBase; the local cache dropped the image to
        // fit the storage quota. It reloads with the next online sync.
        <div className="x-receipt" onClick={ev=>{ev.stopPropagation();onEdit();}} style={{cursor:"pointer"}}>
          <MIcon n="receipt" s={15} /><span className="x-receipt-lab">Receipt synced — reloads online</span>
        </div>
      ):(
        <div className="x-receipt missing" onClick={ev=>{ev.stopPropagation();onEdit();}} style={{cursor:"pointer"}}>
          <MIcon n="camera" s={15} /><span className="x-receipt-lab">Add receipt</span>
        </div>
      )}
    </div>
  );
}

// OTTracker agenda entry card (reads this app's entry shape)
export function FeedEntryCard({ e, onEdit, onDelete }) {
  const abs = isAbs(e.attendanceCode);
  const kind = abs ? "absence" : (e.attendanceCode==="0804" ? "standby" : "ot");
  const bm=calcMins(e.beforeStart,e.beforeEnd), am=calcMins(e.afterStart,e.afterEnd), dm=calcMins(e.duringStart,e.duringEnd);
  const cc = OT_CODES.find(c=>c.value===e.otCode);
  const blocks = [];
  if(bm>0) blocks.push(["up","Before",e.beforeStart,e.beforeEnd]);
  if(am>0) blocks.push(["down","After",e.afterStart,e.afterEnd]);
  if(dm>0) blocks.push(["dot","During",e.duringStart,e.duringEnd]);
  const hasTags = (!abs) || !!e.orderProject;
  return (
    <div className="c-card" onClick={onEdit} style={{cursor:"pointer"}}>
      <div className="c-card-head">
        <span className={"typepill k-"+kind}>
          <span className="tp-dot" />
          <span className="tp-code">{e.attendanceCode}</span>
          {SHORT_CODE[e.attendanceCode]}
        </span>
        <span style={{display:"flex",alignItems:"center",gap:8}}>
          <span className="c-hrs">{fmtHrs(entryHrs(e))}<i>h</i></span>
          {onDelete&&<button className="x-card-del" aria-label="Delete" onClick={ev=>{ev.stopPropagation();onDelete();}}>×</button>}
        </span>
      </div>
      {blocks.length>0&&(
        <div className="c-blocks">
          {blocks.map(([mk,lab,s,en])=>(
            <div className="c-block" key={lab}>
              <span className={"bmark "+mk} />
              <span className="c-block-lab">{lab}</span>
              <span className="c-block-time">{fmtTime(s)}<i>→</i>{fmtTime(en)}</span>
            </div>
          ))}
        </div>
      )}
      {hasTags&&(
        <div className="c-tags">
          {!abs&&cc&&<span className={"c-tag cc cc-"+cc.value}><span className="cc-dot" />{(cc.label.split("—")[1]||cc.label).trim()} · {cc.costCentre}</span>}
          {!abs&&<span className={"c-tag "+(e.compWageType==="1"?"banked":"paid")}>{e.compWageType==="1"?"Banked":"Paid"}</span>}
          {e.orderProject&&<span className="c-tag">#{e.orderProject}</span>}
        </div>
      )}
      {e.reason&&<div className="c-reason">“{e.reason}”</div>}
    </div>
  );
}

/* ══════════════════════════════════════════════════════
   Error Boundary — catches JS crashes so the screen never
   goes blank. Shows a friendly recovery UI instead.
══════════════════════════════════════════════════════ */
export class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { error: null, info: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    this.setState({ info });
    console.error("[OTTracker] Uncaught error:", error, info);
  }
  render() {
    if (!this.state.error) return this.props.children;
    const msg   = this.state.error?.message || String(this.state.error);
    const stack = this.state.info?.componentStack || "";
    const copy  = () => navigator.clipboard?.writeText(`${msg}\n${stack}`).catch(()=>{});
    return (
      <div style={{minHeight:"100vh",background:"#0f1117",color:"#e8eaf0",display:"flex",alignItems:"center",justifyContent:"center",fontFamily:"system-ui,sans-serif",padding:24}}>
        <div style={{maxWidth:420,width:"100%",textAlign:"center"}}>
          <div style={{fontSize:40,marginBottom:16}}>⚠️</div>
          <div style={{fontSize:20,fontWeight:700,marginBottom:8}}>Something went wrong</div>
          <div style={{fontSize:14,color:"#8b90a0",marginBottom:28,lineHeight:1.5}}>
            OTTracker hit an unexpected error. Your data is safe — it's saved locally and in PocketBase.
          </div>
          <div style={{display:"flex",gap:10,justifyContent:"center",flexWrap:"wrap"}}>
            <button onClick={()=>window.location.reload()}
              style={{background:"#2a6fdb",color:"#fff",border:"none",borderRadius:10,padding:"12px 24px",fontSize:14,fontWeight:600,cursor:"pointer"}}>
              Refresh app
            </button>
            <button onClick={copy}
              style={{background:"#1e2230",color:"#8b90a0",border:"1px solid #2e3348",borderRadius:10,padding:"12px 24px",fontSize:14,cursor:"pointer"}}>
              Copy error details
            </button>
          </div>
          <details style={{marginTop:28,textAlign:"left"}}>
            <summary style={{fontSize:12,color:"#555e78",cursor:"pointer",userSelect:"none"}}>Error details</summary>
            <pre style={{marginTop:8,padding:12,background:"#1a1d28",borderRadius:8,fontSize:11,color:"#6b7280",overflowX:"auto",whiteSpace:"pre-wrap",wordBreak:"break-word"}}>
              {msg}{stack}
            </pre>
          </details>
        </div>
      </div>
    );
  }
}
